import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import RAPIER from '@dimforge/rapier3d-compat';
import {
  DEFAULT_PARKING_BARRIERS,
  DEFAULT_PARKING_ISLANDS,
  DEFAULT_PARKED_VEHICLES,
  DEFAULT_PARKING_TASKS,
  PARKING_COURSE_BOUNDS,
  type ParkingTaskDefinition,
} from './parkingData';

export interface ParkingVehicleState {
  position: { x: number; y: number; z: number };
  yaw: number;
  speedKmh: number;
  angularSpeed: number;
  /** 1 = aufrecht; ein umgekipptes Fahrzeug darf nie als geparkt zaehlen. */
  uprightness: number;
  /** Mindestens ein Rad hat sicheren Bodenkontakt. */
  grounded: boolean;
  halfWidth: number;
  halfLength: number;
}

export interface ParkingStatus {
  title: string;
  instruction: string;
  taskIndex: number;
  taskCount: number;
  holdProgress: number;
  parkedCorrectly: boolean;
}

export interface ParkingSystemOptions {
  origin: { x: number; z: number };
  onTaskComplete: (task: ParkingTaskDefinition) => void;
}

/** Parking-Spielmodus mit echten Kollisionsfahrzeugen und fuenf Aufgaben. */
export class ParkingSystem {
  readonly spawn: { x: number; y: number; z: number };
  readonly spawnYaw = Math.PI;

  private readonly group = new THREE.Group();
  private readonly target = new THREE.Group();
  private readonly guidance = new THREE.Group();
  private readonly guideArrows: THREE.InstancedMesh;
  private readonly tasks = DEFAULT_PARKING_TASKS;
  private readonly origin: { x: number; z: number };
  private readonly onTaskComplete: (task: ParkingTaskDefinition) => void;
  private noseLabel: THREE.Sprite | undefined;
  private entryLabel: THREE.Sprite | undefined;
  private taskIndex = 0;
  private holdTime = 0;
  private enabled = false;
  private status: ParkingStatus;

  constructor(scene: THREE.Scene, world: RAPIER.World, options: ParkingSystemOptions) {
    this.origin = options.origin;
    this.onTaskComplete = options.onTaskComplete;
    this.spawn = { x: options.origin.x, y: 1.35, z: options.origin.z + 15 };
    this.status = this.makeStatus(false);
    this.buildCourse(world);
    this.buildParkedCars(world);
    this.buildTarget();
    this.guideArrows = this.buildGuideArrows();
    this.guidance.add(this.guideArrows);
    this.group.add(this.target, this.guidance);
    scene.add(this.group);
    this.applyTaskVisual();
    this.setEnabled(false);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.target.visible = enabled;
    this.guidance.visible = enabled;
    if (!enabled) this.holdTime = 0;
  }

  reset(): void {
    this.taskIndex = 0;
    this.holdTime = 0;
    this.applyTaskVisual();
    this.status = this.makeStatus(false);
  }

  update(vehicle: ParkingVehicleState, dt: number): ParkingStatus {
    if (!this.enabled) return this.status;
    const task = this.tasks[this.taskIndex] ?? this.tasks[0];
    if (!task) return this.status;
    const target = task.target;
    const dx = vehicle.position.x - (this.origin.x + target.x);
    const dz = vehicle.position.z - (this.origin.z + target.z);
    const sin = Math.sin(-target.yaw);
    const cos = Math.cos(-target.yaw);
    const localX = dx * cos - dz * sin;
    const localZ = dx * sin + dz * cos;
    const relativeYaw = wrapAngle(vehicle.yaw - target.yaw);
    const footprintCos = Math.abs(Math.cos(relativeYaw));
    const footprintSin = Math.abs(Math.sin(relativeYaw));
    const projectedHalfWidth = footprintCos * vehicle.halfWidth + footprintSin * vehicle.halfLength;
    const projectedHalfLength = footprintSin * vehicle.halfWidth + footprintCos * vehicle.halfLength;
    const positionOk =
      Math.abs(localX) + projectedHalfWidth <= target.width / 2 + task.boundaryToleranceM &&
      Math.abs(localZ) + projectedHalfLength <= target.length / 2 + task.boundaryToleranceM;
    const angleError = Math.abs(relativeYaw);
    const aligned = angleError <= (task.angleToleranceDeg * Math.PI) / 180;
    const stopped = vehicle.speedKmh <= task.stopSpeedKmh && vehicle.angularSpeed <= task.maxAngularSpeedRad;
    const safelyPlaced = vehicle.uprightness > 0.75 && vehicle.grounded;
    let correct = positionOk && aligned && stopped && safelyPlaced;
    this.holdTime = correct ? this.holdTime + dt : 0;
    const progress = Math.min(1, this.holdTime / task.holdSeconds);
    this.setTargetFeedback(positionOk, aligned, progress);

    if (progress >= 1) {
      this.onTaskComplete(task);
      this.taskIndex = (this.taskIndex + 1) % this.tasks.length;
      this.holdTime = 0;
      this.applyTaskVisual();
      correct = false;
    }
    this.status = this.makeStatus(correct);
    return this.status;
  }

  get currentStatus(): ParkingStatus { return this.status; }

  /**
   * Bounds-Backup fuer `Game`: Die physischen Schutzkanten halten das Auto
   * normalerweise im Parcours. Bei einem sehr harten Physikstoss kann Game
   * hiermit trotzdem sicher zum Parkplatz-Spawn zuruecksetzen.
   */
  isOutsideCourse(
    position: { x: number; y?: number; z: number },
    safetyMarginM = 0,
  ): boolean {
    const localX = position.x - this.origin.x;
    const localZ = position.z - this.origin.z;
    return (position.y ?? 0) < -1.5 ||
      localX < PARKING_COURSE_BOUNDS.minX + safetyMarginM ||
      localX > PARKING_COURSE_BOUNDS.maxX - safetyMarginM ||
      localZ < PARKING_COURSE_BOUNDS.minZ + safetyMarginM ||
      localZ > PARKING_COURSE_BOUNDS.maxZ - safetyMarginM;
  }

  private makeStatus(correct: boolean): ParkingStatus {
    const task = this.tasks[this.taskIndex] ?? this.tasks[0];
    return {
      title: task?.title.toUpperCase() ?? 'PARKEN',
      instruction: correct ? 'STEHEN BLEIBEN …' : (task?.instruction.toUpperCase() ?? ''),
      taskIndex: this.taskIndex,
      taskCount: this.tasks.length,
      holdProgress: task ? Math.min(1, this.holdTime / task.holdSeconds) : 0,
      parkedCorrectly: correct,
    };
  }

  private buildTarget(): void {
    const fill = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffc21a, transparent: true, opacity: 0.24, depthWrite: false }),
    );
    fill.name = 'fill';
    fill.rotation.x = -Math.PI / 2;
    fill.position.y = 0.075;
    fill.userData.parkingFeedback = true;
    this.target.add(fill);
    const edgeMaterial = new THREE.MeshBasicMaterial({ color: 0xffdf55 });
    for (let i = 0; i < 4; i++) {
      const edge = new THREE.Mesh(new THREE.BoxGeometry(1, 0.045, 1), edgeMaterial);
      edge.name = `edge-${i}`;
      edge.position.y = 0.09;
      edge.userData.parkingFeedback = true;
      this.target.add(edge);
    }

    const noseMaterial = new THREE.MeshBasicMaterial({ color: NOSE_COLOR, depthWrite: false });
    const noseLine = new THREE.Mesh(new THREE.BoxGeometry(1, 0.055, 1), noseMaterial);
    noseLine.name = 'nose-line';
    noseLine.position.y = 0.115;
    this.target.add(noseLine);
    const noseArrow = new THREE.Mesh(makeGroundArrowGeometry(), noseMaterial);
    noseArrow.name = 'nose-arrow';
    noseArrow.position.y = 0.125;
    noseArrow.scale.setScalar(0.72);
    this.target.add(noseArrow);

    const entryMaterial = new THREE.MeshBasicMaterial({ color: ENTRY_COLOR, depthWrite: false });
    const entryLine = new THREE.Mesh(new THREE.BoxGeometry(1, 0.06, 1), entryMaterial);
    entryLine.name = 'entry-line';
    entryLine.position.y = 0.12;
    this.target.add(entryLine);
    const entryArrow = new THREE.Mesh(makeGroundArrowGeometry(), entryMaterial);
    entryArrow.name = 'entry-arrow';
    entryArrow.position.y = 0.13;
    this.target.add(entryArrow);

    this.noseLabel = makeLabelSprite('NASE  /  VORNE', '#e85d24');
    this.entryLabel = makeLabelSprite('EINFAHRT', '#168a55');
    if (this.noseLabel) this.target.add(this.noseLabel);
    if (this.entryLabel) this.target.add(this.entryLabel);
  }

  private applyTaskVisual(): void {
    const task = this.tasks[this.taskIndex] ?? this.tasks[0];
    if (!task) return;
    const { target } = task;
    this.target.position.set(this.origin.x + target.x, 0, this.origin.z + target.z);
    this.target.rotation.y = target.yaw;
    this.setTargetFeedback(false, false, 0);
    this.applyGuidePath(task);
    const fill = this.target.getObjectByName('fill');
    fill?.scale.set(target.width, target.length, 1);
    const horizontalScale = new THREE.Vector3(target.width, 1, 0.12);
    const verticalScale = new THREE.Vector3(0.12, 1, target.length);
    const edges = [
      { p: [0, target.length / 2] as const, s: horizontalScale },
      { p: [0, -target.length / 2] as const, s: horizontalScale },
      { p: [target.width / 2, 0] as const, s: verticalScale },
      { p: [-target.width / 2, 0] as const, s: verticalScale },
    ];
    for (let i = 0; i < edges.length; i++) {
      const edge = this.target.getObjectByName(`edge-${i}`);
      const config = edges[i];
      if (!edge || !config) continue;
      edge.position.set(config.p[0], 0.09, config.p[1]);
      edge.scale.copy(config.s);
    }

    const noseLine = this.target.getObjectByName('nose-line');
    noseLine?.position.set(0, 0.115, target.length / 2 - 0.3);
    noseLine?.scale.set(target.width * 0.7, 1, 0.34);
    const noseArrow = this.target.getObjectByName('nose-arrow');
    noseArrow?.position.set(0, 0.125, target.length / 2 - 1.15);
    this.noseLabel?.position.set(0, 1.12, target.length / 2 - 0.35);

    // Vorwaerts wird die hintere Kante zuerst ueberfahren. Beim Rueckwaerts-
    // und Parallelparken kommt das Heck von der Fahrzeugfront-Seite hinein.
    const entrySide = task.maneuver === 'forward' ? -1 : 1;
    const entryZ = entrySide * (target.length / 2 + 0.34);
    const entryLine = this.target.getObjectByName('entry-line');
    entryLine?.position.set(0, 0.12, entryZ);
    entryLine?.scale.set(target.width * 0.92, 1, 0.3);
    const entryArrow = this.target.getObjectByName('entry-arrow');
    entryArrow?.position.set(0, 0.13, entrySide * (target.length / 2 + 1.42));
    entryArrow?.rotation.set(0, entrySide > 0 ? Math.PI : 0, 0);
    if (this.entryLabel) {
      this.entryLabel.position.set(0, 1.12, entrySide * (target.length / 2 + 1.45));
      setLabelSpriteText(
        this.entryLabel,
        task.maneuver === 'forward' ? 'EINFAHRT  /  VORWÄRTS' : 'EINFAHRT  /  HECK ZUERST',
        '#168a55',
      );
    }
  }

  private setTargetFeedback(positionOk: boolean, aligned: boolean, progress: number): void {
    const color = progress > 0.98 ? 0x31d66b : positionOk && aligned ? 0x7ee45b : 0xffc21a;
    this.target.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      if (object.userData.parkingFeedback !== true) return;
      const material = object.material;
      if (material instanceof THREE.MeshBasicMaterial) material.color.setHex(color);
    });
  }

  private buildGuideArrows(): THREE.InstancedMesh {
    const arrows = new THREE.InstancedMesh(
      makeGroundArrowGeometry(),
      new THREE.MeshBasicMaterial({
        color: GUIDE_COLOR,
        transparent: true,
        opacity: 0.82,
        depthWrite: false,
      }),
      MAX_GUIDE_ARROWS,
    );
    arrows.name = 'recommended-route';
    arrows.frustumCulled = false;
    arrows.count = 0;
    return arrows;
  }

  private applyGuidePath(task: ParkingTaskDefinition): void {
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3(0.86, 0.86, 0.86);
    let arrowIndex = 0;
    for (let segmentIndex = 1; segmentIndex < task.approachPath.length; segmentIndex++) {
      const from = task.approachPath[segmentIndex - 1];
      const to = task.approachPath[segmentIndex];
      if (!from || !to) continue;
      const dx = to.x - from.x;
      const dz = to.z - from.z;
      const distance = Math.hypot(dx, dz);
      if (distance < 0.2) continue;
      const yaw = Math.atan2(dx, dz);
      quaternion.setFromAxisAngle(UP, yaw);
      const arrowCount = Math.max(1, Math.floor(distance / GUIDE_ARROW_SPACING));
      for (let step = 0; step < arrowCount && arrowIndex < MAX_GUIDE_ARROWS; step++) {
        const t = Math.min(0.94, (step + 0.62) / arrowCount);
        position.set(
          this.origin.x + from.x + dx * t,
          0.105,
          this.origin.z + from.z + dz * t,
        );
        matrix.compose(position, quaternion, scale);
        this.guideArrows.setMatrixAt(arrowIndex++, matrix);
      }
    }
    this.guideArrows.count = arrowIndex;
    this.guideArrows.instanceMatrix.needsUpdate = true;
  }

  private buildCourse(world: RAPIER.World): void {
    const islandFill = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshStandardMaterial({ color: 0xb79d55, roughness: 1 }),
      DEFAULT_PARKING_ISLANDS.length,
    );
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion().setFromAxisAngle(RIGHT, -Math.PI / 2);
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    for (let i = 0; i < DEFAULT_PARKING_ISLANDS.length; i++) {
      const island = DEFAULT_PARKING_ISLANDS[i];
      if (!island) continue;
      position.set(this.origin.x + island.x, 0.07, this.origin.z + island.z);
      scale.set(island.width, island.length, 1);
      matrix.compose(position, quaternion, scale);
      islandFill.setMatrixAt(i, matrix);
    }
    islandFill.receiveShadow = true;

    const barriers = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshStandardMaterial({ color: 0xdce0df, roughness: 0.82, metalness: 0.02 }),
      DEFAULT_PARKING_BARRIERS.length,
    );
    const safetyCaps = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xf3c528 }),
      DEFAULT_PARKING_BARRIERS.length,
    );
    barriers.castShadow = true;
    barriers.receiveShadow = true;
    quaternion.identity();
    for (let i = 0; i < DEFAULT_PARKING_BARRIERS.length; i++) {
      const barrier = DEFAULT_PARKING_BARRIERS[i];
      if (!barrier) continue;
      const x = this.origin.x + barrier.x;
      const z = this.origin.z + barrier.z;
      position.set(x, barrier.height / 2, z);
      scale.set(barrier.width, barrier.height, barrier.length);
      matrix.compose(position, quaternion, scale);
      barriers.setMatrixAt(i, matrix);
      position.y = barrier.height + 0.018;
      scale.set(barrier.width * 0.94, 0.035, barrier.length * 0.94);
      matrix.compose(position, quaternion, scale);
      safetyCaps.setMatrixAt(i, matrix);

      const body = world.createRigidBody(
        RAPIER.RigidBodyDesc.fixed().setTranslation(x, barrier.height / 2, z),
      );
      world.createCollider(
        RAPIER.ColliderDesc.cuboid(barrier.width / 2, barrier.height / 2, barrier.length / 2)
          .setFriction(0.95)
          .setRestitution(0),
        body,
      );
    }
    this.group.add(islandFill, barriers, safetyCaps);
  }

  private buildParkedCars(world: RAPIER.World): void {
    const count = DEFAULT_PARKED_VEHICLES.length;
    const lower = new THREE.InstancedMesh(
      new RoundedBoxGeometry(1, 1, 1, 2, 0.12),
      new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.45, roughness: 0.3 }),
      count,
    );
    const cabin = new THREE.InstancedMesh(
      new RoundedBoxGeometry(1, 1, 1, 2, 0.1),
      new THREE.MeshPhysicalMaterial({ color: 0x243748, roughness: 0.18, metalness: 0.1 }),
      count,
    );
    const wheelCount = count * 4;
    const wheels = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.34, 0.34, 0.22, 14).rotateZ(Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0x121417, roughness: 0.92 }),
      wheelCount,
    );
    lower.castShadow = cabin.castShadow = wheels.castShadow = true;
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    let wheelIndex = 0;
    for (let i = 0; i < count; i++) {
      const car = DEFAULT_PARKED_VEHICLES[i];
      if (!car) continue;
      const x = this.origin.x + car.x;
      const z = this.origin.z + car.z;
      quaternion.setFromAxisAngle(UP, car.yaw);
      position.set(x, 0.56, z);
      scale.set(car.width, 0.58, car.length);
      matrix.compose(position, quaternion, scale);
      lower.setMatrixAt(i, matrix);
      lower.setColorAt(i, new THREE.Color(car.color));
      const cabinLength = car.style === 'van' ? car.length * 0.72 : car.length * 0.5;
      const cabinZ = car.style === 'sedan' ? -0.12 : 0;
      position.set(cabinZ * Math.sin(car.yaw) + x, car.height * 0.7, cabinZ * Math.cos(car.yaw) + z);
      scale.set(car.width * 0.82, car.height * 0.52, cabinLength);
      matrix.compose(position, quaternion, scale);
      cabin.setMatrixAt(i, matrix);
      for (const side of [-1, 1]) {
        for (const end of [-1, 1]) {
          const local = new THREE.Vector3(side * car.width * 0.48, 0.39, end * car.length * 0.31).applyQuaternion(quaternion);
          position.set(x + local.x, local.y, z + local.z);
          matrix.compose(position, quaternion, ONE);
          wheels.setMatrixAt(wheelIndex++, matrix);
        }
      }
      const body = world.createRigidBody(
        RAPIER.RigidBodyDesc.fixed().setTranslation(x, car.height / 2, z).setRotation({ x: 0, y: Math.sin(car.yaw / 2), z: 0, w: Math.cos(car.yaw / 2) }),
      );
      // Fixed bodies sind auch bei wiederholtem Anfahren unverschiebbar.
      world.createCollider(
        RAPIER.ColliderDesc.cuboid(car.width / 2, car.height / 2, car.length / 2)
          .setFriction(0.95)
          .setRestitution(0),
        body,
      );
    }
    this.group.add(lower, cabin, wheels);
  }
}

function wrapAngle(value: number): number {
  let angle = value;
  while (angle > Math.PI) angle -= Math.PI * 2;
  while (angle < -Math.PI) angle += Math.PI * 2;
  return angle;
}

function makeGroundArrowGeometry(): THREE.ShapeGeometry {
  // Die Form zeigt vor der X-Rotation nach -Y und danach auf dem Boden in +Z.
  const shape = new THREE.Shape();
  shape.moveTo(-0.32, 1.05);
  shape.lineTo(0.32, 1.05);
  shape.lineTo(0.32, -0.08);
  shape.lineTo(0.82, -0.08);
  shape.lineTo(0, -1.18);
  shape.lineTo(-0.82, -0.08);
  shape.lineTo(-0.32, -0.08);
  shape.closePath();
  const geometry = new THREE.ShapeGeometry(shape);
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

function makeLabelSprite(text: string, background: string): THREE.Sprite | undefined {
  const texture = makeLabelTexture(text, background);
  if (!texture) return undefined;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    alphaTest: 0.08,
  }));
  sprite.scale.set(5.2, 1.12, 1);
  sprite.renderOrder = 4;
  return sprite;
}

function setLabelSpriteText(sprite: THREE.Sprite, text: string, background: string): void {
  const texture = makeLabelTexture(text, background);
  if (!texture) return;
  const material = sprite.material;
  material.map?.dispose();
  material.map = texture;
  material.needsUpdate = true;
}

function makeLabelTexture(text: string, background: string): THREE.CanvasTexture | undefined {
  if (typeof document === 'undefined') return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 112;
  const context = canvas.getContext('2d');
  if (!context) return undefined;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = background;
  roundedRect(context, 4, 4, canvas.width - 8, canvas.height - 8, 24);
  context.fill();
  context.strokeStyle = 'rgba(255,255,255,0.92)';
  context.lineWidth = 5;
  context.stroke();
  context.fillStyle = '#ffffff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `800 ${text.length > 22 ? 35 : 42}px system-ui, sans-serif`;
  context.fillText(text, canvas.width / 2, canvas.height / 2 + 1, canvas.width - 34);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const right = x + width;
  const bottom = y + height;
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(right - radius, y);
  context.quadraticCurveTo(right, y, right, y + radius);
  context.lineTo(right, bottom - radius);
  context.quadraticCurveTo(right, bottom, right - radius, bottom);
  context.lineTo(x + radius, bottom);
  context.quadraticCurveTo(x, bottom, x, bottom - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

const ENTRY_COLOR = 0x28d17c;
const NOSE_COLOR = 0xf06a2b;
const GUIDE_COLOR = 0x62f29a;
const GUIDE_ARROW_SPACING = 4.4;
const MAX_GUIDE_ARROWS = 48;
const UP = new THREE.Vector3(0, 1, 0);
const RIGHT = new THREE.Vector3(1, 0, 0);
const ONE = new THREE.Vector3(1, 1, 1);
